import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, ArrowRight, Monitor, Tablet, Smartphone,
  Layers, Layout, Star, ExternalLink, ChevronRight,
  Code2, Loader2,
} from 'lucide-react';
import { OrbisMarkSVG } from '@/components/OrbisLogo';
import { getTemplateById } from '@/data/templates';
import { fetchAndProcessTemplate } from '@/utils/templatePreview';

const CATEGORY_COLORS = {
  'Business':  '#10b981',
  'Creative':  '#f59e0b',
  'Finance':   '#3b82f6',
  'SaaS':      '#7c3aed',
  'Agency':    '#a855f7',
  'Portfolio': '#6366f1',
  'Startup':   '#06b6d4',
};

const VIEWPORT_OPTIONS = [
  { id: 'desktop', label: 'Desktop', icon: Monitor, width: '100%' },
  { id: 'tablet',  label: 'Tablet',  icon: Tablet,  width: 768 },
  { id: 'mobile',  label: 'Mobile',  icon: Smartphone, width: 375 },
];

export default function TemplateDetailPage() {
  const { templateSlug } = useParams();
  const navigate = useNavigate();
  const template = getTemplateById(templateSlug);
  const [viewport, setViewport] = useState('desktop');
  const [currentPageIdx, setCurrentPageIdx] = useState(0);
  const [previewHtml, setPreviewHtml] = useState('');
  const [loadError, setLoadError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const loadPage = useCallback(async (tmpl, pageIdx) => {
    const page = tmpl?.pages?.[pageIdx];
    if (!page?.htmlPath) return;
    setIsLoading(true);
    setPreviewHtml('');
    setLoadError(false);
    try {
      const html = await fetchAndProcessTemplate(page.htmlPath, tmpl);
      setPreviewHtml(html);
    } catch {
      setLoadError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load first page on mount
  useEffect(() => {
    if (!template) return;
    setCurrentPageIdx(0);
    loadPage(template, 0);
  }, [template, loadPage]);

  // Handle in-preview navigation clicks
  useEffect(() => {
    if (!template) return;
    const handler = (e) => {
      if (e.data?.type !== 'orbis_preview_nav') return;
      const href = e.data.href;
      if (!href || href.startsWith('#')) return;
      const baseDir = template.pages[currentPageIdx]?.htmlPath
        ?.substring(0, template.pages[currentPageIdx].htmlPath.lastIndexOf('/') + 1) ?? '';
      const targetUrl = href.startsWith('http') ? href : baseDir + href.replace(/^\.\//,'').replace(/^\//,'');
      fetchAndProcessTemplate(targetUrl, template).then(setPreviewHtml).catch(() => setLoadError(true));
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [template, currentPageIdx]);

  // Template not found
  if (!template) {
    return (
      <div style={{ minHeight: '100vh', background: '#080808', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
        <p style={{ color: '#555', fontSize: '14px' }}>Template not found</p>
        <button onClick={() => navigate('/templates')} style={{ color: '#7c3aed', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px' }}>
          ← Back to Templates
        </button>
      </div>
    );
  }

  const accentColor = CATEGORY_COLORS[template.category] || '#7c3aed';
  const vpOption = VIEWPORT_OPTIONS.find(v => v.id === viewport);
  const vpWidth = vpOption?.width ?? '100%';

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#080808', overflow: 'hidden' }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      {/* Top Bar */}
      <div style={{
        height: 52, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 20px',
        background: 'var(--surface-0)',
        borderBottom: '1px solid var(--orbis-border)',
      }}>
        {/* Left: back + breadcrumb */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button
            onClick={() => navigate('/templates')}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: '6px', background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: '12px', fontFamily: 'var(--orbis-font)' }}
          >
            <ArrowLeft size={13} /> Templates
          </button>
          <span style={{ color: '#2a2a2a', fontSize: '12px' }}>/</span>
          <span style={{ fontSize: '12px', color: '#888' }}>{template.name}</span>
        </div>
        {/* Center: viewport toggle */}
        <div style={{ display: 'flex', gap: 1, background: '#0d0d0d', border: '1px solid #1a1a1a', borderRadius: '8px', padding: '2px' }}>
          {VIEWPORT_OPTIONS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setViewport(id)}
              title={label}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '5px 14px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                fontSize: '12px', fontFamily: 'var(--orbis-font)',
                background: viewport === id ? '#1a1a1a' : 'transparent',
                color: viewport === id ? '#ccc' : '#444',
                transition: 'all 0.15s',
              }}
            >
              <Icon size={13} />
            </button>
          ))}
        </div>
        {/* Right: logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }} onClick={() => navigate('/dashboard')}>
          <OrbisMarkSVG size={20} />
          <span style={{ fontSize: '13px', color: '#555', fontFamily: 'var(--orbis-font)' }}>Orbis</span>
        </div>
      </div>
      {/* Main content */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Preview area */}
        <main style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#111' }}>
          {/* Page tab bar (multi-page templates) */}
          {template.pages.length > 1 && (
            <div style={{ height: 40, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 2, padding: '0 12px', background: '#0d0d0d', borderBottom: '1px solid #1a1a1a', overflowX: 'auto' }}>
              {template.pages.map((page, i) => (
                <button
                  key={page.id}
                  onClick={() => { setCurrentPageIdx(i); loadPage(template, i); }}
                  style={{
                    padding: '4px 14px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                    fontSize: '12px', fontFamily: 'var(--orbis-font)', whiteSpace: 'nowrap',
                    background: currentPageIdx === i ? 'rgba(255,255,255,0.06)' : 'transparent',
                    color: currentPageIdx === i ? '#ccc' : '#444',
                    transition: 'all 0.15s',
                  }}
                >
                  {page.name}
                </button>
              ))}
            </div>
          )}
          {/* iframe container */}
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: viewport !== 'desktop' ? 20 : 0, overflow: 'hidden' }}>
            <div style={{
              width: vpWidth,
              maxWidth: '100%',
              height: '100%',
              transition: 'width 0.35s cubic-bezier(0.4,0,0.2,1)',
              borderRadius: viewport !== 'desktop' ? '12px' : 0,
              border: viewport !== 'desktop' ? '1px solid #2a2a2a' : 'none',
              overflow: 'hidden',
              background: '#fff',
              position: 'relative',
            }}>
              {/* Loading state */}
              {isLoading && (
                <div style={{ position: 'absolute', inset: 0, background: '#111', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14 }}>
                  <div style={{ width: 32, height: 32, border: '2px solid #2a2a2a', borderTopColor: accentColor, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                  <p style={{ fontSize: '12px', color: '#444' }}>Loading preview…</p>
                </div>
              )}
              {/* Error state */}
              {loadError && !isLoading && (
                <div style={{ position: 'absolute', inset: 0, background: '#111', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
                  <ExternalLink size={28} style={{ color: '#333' }} />
                  <p style={{ fontSize: '13px', color: '#555' }}>Preview unavailable</p>
                  <a
                    href={template.pages[currentPageIdx]?.htmlPath}
                    target="_blank" rel="noopener noreferrer"
                    style={{ fontSize: '12px', color: accentColor }}
                  >
                    Open in new tab ↗
                  </a>
                </div>
              )}
              {/* Live iframe */}
              {previewHtml && !isLoading && (
                <iframe
                  key={`${template.id}-${currentPageIdx}`}
                  srcDoc={previewHtml}
                  title={`${template.name} Preview`}
                  style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
                  sandbox="allow-scripts allow-same-origin"
                />
              )}
            </div>
          </div>
        </main>
        {/* Right sidebar */}
        <aside style={{
          width: 300,
          flexShrink: 0,
          borderLeft: '1px solid #1a1a1a',
          background: '#0a0a0a',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <div style={{ flex: 1, padding: '24px 20px' }}>
            {/* Category + tech badge */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <span style={{ fontSize: '10px', fontFamily: 'var(--orbis-mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: accentColor }}>
                {template.category}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '10px', fontFamily: 'var(--orbis-mono)', color: '#444', background: '#111', border: '1px solid #1a1a1a', padding: '2px 8px', borderRadius: '4px' }}>
                <Code2 size={9} /> {template.techStack || 'HTML5'}
              </span>
            </div>
            {/* Name + tagline */}
            <h1 style={{ fontSize: '20px', fontWeight: 700, color: '#e5e5e5', letterSpacing: '-0.01em', marginBottom: 8, fontFamily: 'var(--orbis-font)' }}>
              {template.name}
            </h1>
            <p style={{ fontSize: '13px', color: '#555', lineHeight: 1.55, marginBottom: 20 }}>
              {template.tagline}
            </p>
            <div style={{ width: '100%', height: 1, background: '#1a1a1a', marginBottom: 20 }} />
            {/* Meta info */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
              {[
                { Icon: Layers, label: `${template.pageCount} ${template.pageCount === 1 ? 'page' : 'pages'}` },
                { Icon: Layout, label: template.style },
                { Icon: Star,   label: `${template.popularity.toLocaleString()} uses` },
              ].map(({ Icon, label }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '12px', color: '#555' }}>
                  <Icon size={13} style={{ color: '#333', flexShrink: 0 }} /> {label}
                </div>
              ))}
              {/* Tier badge */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '12px', color: '#555' }}>
                <span style={{
                  fontSize: '10px', padding: '2px 8px', borderRadius: '100px',
                  background: template.tier === 'pro' ? '#1a1500' : '#0d2010',
                  color: template.tier === 'pro' ? '#f59e0b' : '#4ade80',
                  border: `1px solid ${template.tier === 'pro' ? 'rgba(245,158,11,0.2)' : 'rgba(74,222,128,0.2)'}`,
                }}>
                  {template.tier?.toUpperCase() || 'FREE'}
                </span>
              </div>
            </div>
            <div style={{ width: '100%', height: 1, background: '#1a1a1a', marginBottom: 20 }} />
            {/* Color palette */}
            <div style={{ marginBottom: 20 }}>
              <p style={{ fontSize: '10px', fontFamily: 'var(--orbis-mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#444', marginBottom: 10 }}>
                Default Colors
              </p>
              <div style={{ display: 'flex', gap: 6 }}>
                {Object.entries(template.defaultBrandKit.colors).map(([key, c]) => (
                  <div
                    key={key}
                    title={`${key}: ${c}`}
                    style={{ flex: 1, height: 28, background: c, borderRadius: '6px', border: '1px solid rgba(255,255,255,0.06)' }}
                  />
                ))}
              </div>
            </div>
            {/* Typography */}
            <div style={{ marginBottom: 20 }}>
              <p style={{ fontSize: '10px', fontFamily: 'var(--orbis-mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#444', marginBottom: 10 }}>
                Typography
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                  <span style={{ color: '#444' }}>Heading</span>
                  <span style={{ color: '#888', fontFamily: template.defaultBrandKit.fonts.heading }}>{template.defaultBrandKit.fonts.heading}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                  <span style={{ color: '#444' }}>Body</span>
                  <span style={{ color: '#888', fontFamily: template.defaultBrandKit.fonts.body }}>{template.defaultBrandKit.fonts.body}</span>
                </div>
              </div>
            </div>
            {/* Tags */}
            {template.tags?.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <p style={{ fontSize: '10px', fontFamily: 'var(--orbis-mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#444', marginBottom: 10 }}>
                  Tags
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {template.tags.map(tag => (
                    <span key={tag} style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '100px', background: '#111', border: '1px solid #1a1a1a', color: '#555', fontFamily: 'var(--orbis-mono)' }}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {/* Pages list (if multi-page) */}
            {template.pages.length > 1 && (
              <>
                <div style={{ width: '100%', height: 1, background: '#1a1a1a', marginBottom: 20 }} />
                <div style={{ marginBottom: 20 }}>
                  <p style={{ fontSize: '10px', fontFamily: 'var(--orbis-mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#444', marginBottom: 10 }}>
                    Included Pages
                  </p>
                  {template.pages.map((page, i) => (
                    <button
                      key={page.id}
                      onClick={() => { setCurrentPageIdx(i); loadPage(template, i); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                        padding: '7px 10px', borderRadius: '6px', border: 'none',
                        background: currentPageIdx === i ? 'rgba(255,255,255,0.04)' : 'transparent',
                        cursor: 'pointer', fontSize: '12px', textAlign: 'left',
                        color: currentPageIdx === i ? '#ccc' : '#555',
                        transition: 'all 0.15s',
                      }}
                    >
                      <div style={{ width: 5, height: 5, borderRadius: '50%', background: currentPageIdx === i ? accentColor : '#2a2a2a', flexShrink: 0 }} />
                      {page.name}
                      {currentPageIdx === i && <ChevronRight size={11} style={{ marginLeft: 'auto', color: '#333' }} />}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          {/* Sticky CTA footer */}
          <div style={{ padding: '16px 20px', borderTop: '1px solid #1a1a1a', background: '#0a0a0a', flexShrink: 0 }}>
            <motion.button
              onClick={() => navigate(`/templates/${template.id}/edit`)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              style={{
                width: '100%', padding: '13px', borderRadius: '10px',
                background: '#f0f0f0', color: '#000', fontSize: '14px',
                fontWeight: 600, border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                fontFamily: 'var(--orbis-font)', marginBottom: 10,
              }}
            >
              Use This Template <ArrowRight size={15} />
            </motion.button>
            <button
              onClick={() => navigate('/templates')}
              style={{ width: '100%', padding: '9px', borderRadius: '10px', background: 'transparent', color: '#444', fontSize: '12px', border: '1px solid #1a1a1a', cursor: 'pointer', fontFamily: 'var(--orbis-font)' }}
            >
              ← Back to Templates
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}